import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
    ActionButton,
    Button,
    Card,
    Checkbox,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
} from "@jhool-io/fe-components";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import BackLink from "../../../components/BackLink/BackLink";
import Navbar from "../../../components/Navbar/Navbar";
import Loader from "../../../shared-ui/Loader/Loader";
import ListState from "../../../components/ListState/ListState";
import { useGetParentClaim } from "../hooks/claims.queries";
import {
    useAddNotesToBulkClaim,
    useSplitClaimsToNewClaim,
    useUpdateClaimStatus,
} from "../hooks/claims.mutations";
import {
    ClaimStatus,
    IParentClaimChildClaim,
    SingleClaim,
} from "../types/claims.types";
import {
    cn,
    formatDate,
    removeEnumUnderscore,
    showMoneyInAppFormat,
} from "../../../utils/helpers";
import {
    getClaimStatusTagStylesForDropdown,
    getClaimCptCodeEntriesFromServiceDetails,
    getClaimsListNavigateUrl,
    getStatusTag,
} from "../helpers/claims.helper";
import ViewClaim from "../components/ViewClaims/ViewClaim";
import { fetchNotesList } from "../../notes/services/notes.services";
import { getCptCodeToDisplay } from "../../notes/helpers/notes.helpers";
import {
    INote,
    INotesListFilterTypes,
    NoteLabels,
    NoteStatus,
    NoteTypes,
} from "../../notes/types/notes.types";
import useToast from "../../../hooks/useToast";
import {
    BillStatus,
    BillStatusLabels,
} from "../../billing/types/billing.types";
import ChevronDownIcon from "../../../components/Icons/ChevronDown";
import PlusIcon from "../../../icons/Plus";
import TimesIcon from "../../../icons/Times";

const PSYCH_NOTE_TYPES: NoteTypes[] = [
    NoteTypes.TESTING_INTAKE_MINOR_INTAKE_PSYCH,
    NoteTypes.INTEGRATING_PATIENT_DATA_PSYCH,
    NoteTypes.ADMINISTRATION_PSYCH,
    NoteTypes.ADMINISTRATION_TECH,
    NoteTypes.SCORING_BY_TECH_PSYCH,
    NoteTypes.SCORING_BY_PSYCH,
    NoteTypes.INTERPRETATION_REPORT_WRITING_PSYCH,
    NoteTypes.FEEDBACK_PSYCH,
    NoteTypes.PAUSE_NOTIFICATION_PSYCH,
    NoteTypes.TERMINATION_OF_TREATMENT_PSYCH,
    NoteTypes.RECORD_OF_DISCLOSURE_PSYCH,
    NoteTypes.NO_SHOW_PSYCH,
];
const CLAIM_STATUS_OPTIONS = Object.values(ClaimStatus) as ClaimStatus[];

function getBillStatusClassName(status?: string) {
    if (!status) {
        return "bg-[--color-neutral-bg] text-[--color-neutral-text]";
    }

    if ([BillStatus.OPEN, BillStatus.CLOSED].includes(status as BillStatus)) {
        return "bg-[--color-neutral-bg] text-[--color-neutral-text]";
    }

    if (
        [
            BillStatus.CARD_PAYMENT_DECLINED,
            BillStatus.CARD_PAYMENT_DECLINED_PR100,
        ].includes(status as BillStatus)
    ) {
        return "bg-[--color-danger-bg] text-[--color-danger-text]";
    }

    if (
        [BillStatus.UNCOLLECTABLE, BillStatus.CREDIT_BALANCE].includes(
            status as BillStatus
        )
    ) {
        return "bg-[--color-info-bg] text-[--color-info-text]";
    }

    return "bg-[--color-warning-bg] text-[--color-warning-text]";
}

type ChildClaimDetails = {
    childClaim: IParentClaimChildClaim;
    claim: {
        charge_amount?: string;
        claim_content?: SingleClaim["claim_content"] | null;
    };
    isLoading: false;
    invoiceStatus?: string;
};

function getClaimCptCodesCompact(
    claim?: ChildClaimDetails["claim"]
): React.ReactNode {
    if (!claim?.claim_content) {
        return null;
    }

    const { serviceDetails } = claim.claim_content as {
        serviceDetails?: unknown;
    };
    const cptCodes = getClaimCptCodeEntriesFromServiceDetails(serviceDetails);

    if (cptCodes.length === 0) {
        return null;
    }

    const cptCodeCountMap = cptCodes.reduce((map, code) => {
        map.set(code, (map.get(code) ?? 0) + 1);
        return map;
    }, new Map<string, number>());

    return (
        <span className="flex flex-wrap items-center gap-1.5">
            {Array.from(cptCodeCountMap.entries()).map(
                ([code, count], index, arr) => (
                    <span
                        key={code}
                        className="inline-flex items-center gap-0.5 text-xs text-[--color-text]"
                    >
                        <span>{code}</span>
                        {count > 1 ? (
                            <span className="rounded-full bg-slate-100 px-1.5 py-0 text-[10px] text-slate-600">
                                x {count}
                            </span>
                        ) : null}
                        {index < arr.length - 1 ? <span>,</span> : null}
                    </span>
                )
            )}
        </span>
    );
}

export default function ViewGroupedClaim() {
    const [searchParams] = useSearchParams();
    const [selectedNoteIdsToAdd, setSelectedNoteIdsToAdd] = useState<string[]>(
        []
    );
    const [storedSelectedChildClaimIds, setStoredSelectedChildClaimIds] =
        useState<string[]>([]);
    const [storedRebillTargetIds, setStoredRebillTargetIds] = useState<
        string[] | null
    >(null);
    const navigate = useNavigate();
    const { claimId, noteId } = useParams();
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const parentClaimId = claimId || "";
    const splitClaimsToNewClaim = useSplitClaimsToNewClaim();
    const addNotesToBulkClaim = useAddNotesToBulkClaim();
    const updateClaimStatus = useUpdateClaimStatus();

    const parentClaimData = useGetParentClaim(
        parentClaimId,
        Boolean(parentClaimId)
    );
    const childClaims = useMemo(
        () => parentClaimData.data?.data.child_claims ?? [],
        [parentClaimData.data?.data.child_claims]
    );

    const childClaimsWithDetails: ChildClaimDetails[] = childClaims.map(
        (childClaim) => ({
            childClaim,
            claim: {
                charge_amount: childClaim.charge_amount,
                claim_content: childClaim.claim_content,
            },
            isLoading: false,
            invoiceStatus: childClaim.invoice_status ?? undefined,
        })
    );

    const activeClaimId = searchParams.get("active_claim_id");
    const selectedChildClaim =
        childClaimsWithDetails.find(
            (child) => child.childClaim.note_id === noteId
        ) ??
        childClaimsWithDetails.find(
            (child) => child.childClaim.claim_id === activeClaimId
        ) ??
        childClaimsWithDetails[0];

    const claimsListUrl = getClaimsListNavigateUrl();
    const parentClaimStatus =
        parentClaimData.data?.data.parent_claim?.claim_status;
    const canModifyChildClaims =
        parentClaimStatus === ClaimStatus.READY_FOR_SUBMISSION;
    const canRebillChildClaims = Boolean(parentClaimStatus);
    const selectedChildClaimIds = canRebillChildClaims
        ? storedSelectedChildClaimIds
        : [];
    const rebillTargetIds = canRebillChildClaims ? storedRebillTargetIds : null;

    const totalChildClaims = childClaims.length;
    const selectedChildCount = selectedChildClaimIds.length;
    const hasChildSelection = selectedChildCount > 0;
    const hasMultipleChildClaims = totalChildClaims > 1;
    const selectionIncludesAllChildren =
        totalChildClaims > 0 && selectedChildCount >= totalChildClaims;
    const selectedChildClaimIdSet = hasChildSelection
        ? new Set(selectedChildClaimIds)
        : null;
    const rebillTargetIdSet = rebillTargetIds ? new Set(rebillTargetIds) : null;
    const rebillTargetDetails = rebillTargetIdSet
        ? childClaimsWithDetails.filter((detail) =>
              rebillTargetIdSet.has(detail.childClaim.claim_id)
          )
        : [];
    const rebillTargetCount = rebillTargetIds?.length ?? 0;
    const rebillTargetTotal = rebillTargetDetails.reduce(
        (sum, detail) => sum + Number(detail.claim?.charge_amount ?? 0),
        0
    );
    const parentClaimClientId =
        parentClaimData.data?.data.parent_claim?.client_id ?? "";
    const parentClaimInsuranceProviderId =
        parentClaimData.data?.data.parent_claim?.insurance_provider_id ?? "";
    const parentClaimExternalReferenceId =
        parentClaimData.data?.data.parent_claim?.external_reference_id?.toUpperCase() ??
        "--";

    const eligibleNotesForBulkClaimQuery = useQuery<INote[]>({
        queryKey: [
            "bulk-claim-eligible-notes",
            parentClaimClientId,
            parentClaimInsuranceProviderId,
        ],
        enabled:
            canModifyChildClaims &&
            Boolean(parentClaimClientId) &&
            Boolean(parentClaimInsuranceProviderId),
        queryFn: async () => {
            const baseFilters: INotesListFilterTypes & {
                show_notes_without_claims: "true";
            } = {
                client_id: parentClaimClientId,
                insurance_provider_id: parentClaimInsuranceProviderId,
                status: [NoteStatus.CODED],
                type: PSYCH_NOTE_TYPES,
                sort_attribute: "date_of_service",
                sort_order: "ASC",
                show_notes_without_claims: "true",
                show_notes_eligible_for_bulk_claim: "true",
            };

            const firstPage = await fetchNotesList(
                {
                    ...baseFilters,
                    page: 1,
                    limit: 100,
                },
                ""
            );

            const notes = [...(firstPage.data ?? [])];
            const totalPages = firstPage.total_pages ?? 1;
            if (totalPages <= 1) {
                return notes;
            }

            const remainingPages = Array.from(
                { length: totalPages - 1 },
                (_, index) => index + 2
            );
            const remainingResponses = await Promise.all(
                remainingPages.map((page) =>
                    fetchNotesList(
                        {
                            ...baseFilters,
                            page,
                            limit: 100,
                        },
                        ""
                    )
                )
            );

            remainingResponses.forEach((response) => {
                notes.push(...(response.data ?? []));
            });

            return notes;
        },
    });

    const existingChildNoteIds = useMemo(
        () => new Set(childClaims.map((childClaim) => childClaim.note_id)),
        [childClaims]
    );

    const eligibleNotesForBulkClaim = useMemo(
        () =>
            (eligibleNotesForBulkClaimQuery.data ?? []).filter(
                (note) =>
                    !note.claim_id &&
                    note.insurance_provider_id ===
                        parentClaimInsuranceProviderId &&
                    !existingChildNoteIds.has(note.note_id)
            ),
        [
            eligibleNotesForBulkClaimQuery.data,
            existingChildNoteIds,
            parentClaimInsuranceProviderId,
        ]
    );

    const hasEligibleNotesToAdd = eligibleNotesForBulkClaim.length > 0;
    const addButtonDisabled =
        !canModifyChildClaims ||
        addNotesToBulkClaim.isLoading ||
        updateClaimStatus.isLoading ||
        eligibleNotesForBulkClaimQuery.isLoading ||
        !hasEligibleNotesToAdd;

    let addButtonTitle: string | undefined;
    if (!canModifyChildClaims) {
        addButtonTitle = "Set group claim to Ready for Submission to add notes";
    } else if (
        !eligibleNotesForBulkClaimQuery.isLoading &&
        !hasEligibleNotesToAdd
    ) {
        addButtonTitle = "No client notes available to add";
    }

    const childClaimMutationInFlight =
        splitClaimsToNewClaim.isLoading ||
        addNotesToBulkClaim.isLoading ||
        updateClaimStatus.isLoading;
    const childRebillActionsDisabled =
        !canRebillChildClaims || childClaimMutationInFlight;
    const bulkRebillDisabled =
        childRebillActionsDisabled || selectionIncludesAllChildren;
    const claimStatusDropdownDisabled =
        addNotesToBulkClaim.isLoading ||
        splitClaimsToNewClaim.isLoading ||
        updateClaimStatus.isLoading;
    const parentClaimStatusStyles = parentClaimStatus
        ? getClaimStatusTagStylesForDropdown(parentClaimStatus)
        : undefined;

    const handleSelectClaim = (nextClaim: IParentClaimChildClaim) => {
        const params = new URLSearchParams(searchParams);
        params.set("active_claim_id", nextClaim.claim_id);
        navigate(
            `/claims/group/${parentClaimId}/${nextClaim.client_id}/${nextClaim.note_id}` +
                `?${params.toString()}`
        );
    };

    useEffect(() => {
        if (!selectedChildClaim?.childClaim?.note_id) {
            return;
        }

        if (noteId === selectedChildClaim.childClaim.note_id) {
            return;
        }

        const params = new URLSearchParams(searchParams);
        params.set("active_claim_id", selectedChildClaim.childClaim.claim_id);
        navigate(
            `/claims/group/${parentClaimId}/${selectedChildClaim.childClaim.client_id}` +
                `/${
                    selectedChildClaim.childClaim.note_id
                }?${params.toString()}`,
            { replace: true }
        );
    }, [navigate, noteId, parentClaimId, searchParams, selectedChildClaim]);

    useEffect(() => {
        setSelectedNoteIdsToAdd((prev) =>
            prev.filter((noteIdToAdd) =>
                eligibleNotesForBulkClaim.some(
                    (eligibleNote) => eligibleNote.note_id === noteIdToAdd
                )
            )
        );
    }, [eligibleNotesForBulkClaim]);

    const handleToggleNoteToAdd = (nextNoteId: string) => {
        setSelectedNoteIdsToAdd((prev) =>
            prev.includes(nextNoteId)
                ? prev.filter((noteIdToAdd) => noteIdToAdd !== nextNoteId)
                : [...prev, nextNoteId]
        );
    };

    const handleAddNotes = () => {
        if (
            !parentClaimId ||
            selectedNoteIdsToAdd.length === 0 ||
            addNotesToBulkClaim.isLoading
        ) {
            return;
        }

        addNotesToBulkClaim.mutate(
            {
                parentClaimId,
                noteIds: selectedNoteIdsToAdd,
            },
            {
                onSuccess: async (response) => {
                    await queryClient.invalidateQueries({
                        queryKey: ["parent-claim", parentClaimId],
                    });
                    await queryClient.invalidateQueries({
                        queryKey: ["claims"],
                    });
                    await queryClient.invalidateQueries({
                        queryKey: ["claims-stats"],
                    });
                    await queryClient.invalidateQueries({
                        queryKey: ["bulk-claim-eligible-notes"],
                    });

                    setSelectedNoteIdsToAdd([]);
                    toast({
                        mode: "success",
                        message:
                            response.message ||
                            "Notes added to grouped claim successfully",
                    });
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not add notes to grouped claim",
                    });
                },
            }
        );
    };

    const handleUpdateParentClaimStatus = (nextStatus: ClaimStatus) => {
        if (
            !parentClaimId ||
            !parentClaimStatus ||
            updateClaimStatus.isLoading ||
            nextStatus === parentClaimStatus
        ) {
            return;
        }

        updateClaimStatus.mutate(
            {
                claim_id: parentClaimId,
                claim_status: nextStatus,
            },
            {
                onSuccess: async (response) => {
                    await queryClient.invalidateQueries({
                        queryKey: ["parent-claim", parentClaimId],
                    });
                    await queryClient.invalidateQueries({
                        queryKey: ["claims"],
                    });
                    await queryClient.invalidateQueries({
                        queryKey: ["claims-stats"],
                    });
                    await queryClient.invalidateQueries({
                        queryKey: ["bulk-claim-eligible-notes"],
                    });

                    toast({
                        mode: "success",
                        message:
                            response.message ||
                            "Claim status updated successfully",
                    });
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not update claim status at this time",
                    });
                },
            }
        );
    };

    const toggleChildClaimSelection = (claimIdToToggle: string) => {
        setStoredSelectedChildClaimIds((prev) =>
            prev.includes(claimIdToToggle)
                ? prev.filter((id) => id !== claimIdToToggle)
                : [...prev, claimIdToToggle]
        );
    };

    const handleConfirmRebill = () => {
        if (
            childRebillActionsDisabled ||
            !parentClaimId ||
            !rebillTargetIds ||
            rebillTargetIds.length === 0 ||
            rebillTargetIds.length >= totalChildClaims
        ) {
            return;
        }

        splitClaimsToNewClaim.mutate(
            {
                parentClaimId,
                claimIds: rebillTargetIds,
            },
            {
                onSuccess: (response) => {
                    const newParentClaimId = response.data?.new_parent_claim_id;
                    if (newParentClaimId) {
                        const params = new URLSearchParams({
                            key: "claims",
                            claims_view: "view",
                            viewFrom: "claims",
                            tab: "submission_ready",
                            claims_sub_tab: "claims",
                            active_claim_id: newParentClaimId,
                        });
                        navigate(
                            `/claims/group/${newParentClaimId}/${parentClaimClientId}?${params.toString()}`
                        );
                    }

                    toast({
                        mode: "success",
                        message:
                            response.message ||
                            "Claims detached into a new claim successfully",
                    });

                    queryClient.invalidateQueries({
                        queryKey: ["parent-claim", parentClaimId],
                    });
                    queryClient.invalidateQueries({
                        queryKey: ["get-claim"],
                    });
                    queryClient.invalidateQueries({
                        predicate: (query) =>
                            query.queryKey[1] === "session-note",
                    });
                    queryClient.invalidateQueries({
                        queryKey: ["claims"],
                    });
                    queryClient.invalidateQueries({
                        queryKey: ["claims-stats"],
                    });
                    queryClient.invalidateQueries({
                        queryKey: ["bulk-claim-eligible-notes"],
                    });
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not detach claims into a new claim",
                    });
                },
                onSettled: () => {
                    setStoredRebillTargetIds(null);
                    setStoredSelectedChildClaimIds([]);
                },
            }
        );
    };

    if (parentClaimData.isLoading) {
        return <Loader />;
    }

    if (parentClaimData.error || !parentClaimData.data?.data.parent_claim) {
        return (
            <ListState
                isError
                emptyMessage="Could not load grouped claim details"
                stateHelperText="Please refresh and try again."
            />
        );
    }

    return (
        <>
            <Dialog open={rebillTargetIds !== null}>
                <DialogContent
                    variant="center"
                    title={`Detach ${rebillTargetCount} ${
                        rebillTargetCount === 1 ? "claim" : "claims"
                    } from the group`}
                    showFooter
                    cancelText="Cancel"
                    saveText="Confirm"
                    onCancelClick={() => setStoredRebillTargetIds(null)}
                    onSaveClick={handleConfirmRebill}
                    handleCloseDialog={() => setStoredRebillTargetIds(null)}
                    isSubmitBtnDisabled={bulkRebillDisabled}
                    isCancelBtnDisabled={splitClaimsToNewClaim.isLoading}
                >
                    <div className="mb-6 flex flex-col gap-3">
                        <div className="grid grid-cols-[1fr_1fr_auto] gap-3 px-3 text-caption font-medium uppercase text-textSec">
                            <span>Date of service</span>
                            <span>CPT code</span>
                            <span className="text-right">Charge</span>
                        </div>
                        <div className="divide-y divide-[--color-stroke] rounded-lg border border-stroke">
                            {rebillTargetDetails.map(
                                ({ childClaim, claim }) => (
                                    <div
                                        key={childClaim.claim_id}
                                        className="grid grid-cols-[1fr_1fr_auto] items-center gap-3 px-3 py-2.5"
                                    >
                                        <span className="text-sm">
                                            {formatDate(
                                                childClaim.date_of_service
                                            )}
                                        </span>
                                        <span className="text-sm">
                                            {getClaimCptCodesCompact(claim) ||
                                                childClaim.cpt_code ||
                                                "--"}
                                        </span>
                                        <span className="shrink-0 text-right text-sm font-semibold">
                                            {claim
                                                ? showMoneyInAppFormat(
                                                      claim.charge_amount ?? 0
                                                  )
                                                : "--"}
                                        </span>
                                    </div>
                                )
                            )}
                            <div className="grid grid-cols-[1fr_1fr_auto] items-center gap-3 px-3 py-2.5">
                                <span className="col-span-2 text-sm font-semibold">
                                    Total
                                </span>
                                <span className="shrink-0 text-right text-sm font-semibold">
                                    {showMoneyInAppFormat(rebillTargetTotal)}
                                </span>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <div className="space-y-[--spacing-items]">
                <Navbar title="Group Claim" />
                <BackLink onClick={() => navigate(claimsListUrl)} />

                <div className="grid grid-cols-[288px_minmax(0,1fr)] gap-[--spacing-items] max-[1000px]:grid-cols-1">
                    <Card classNames="ml-2 h-fit overflow-hidden p-0 bg-[--card-bg-gray]">
                        <div className="p-2">
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                    <p className="text-[10px] font-semibold tracking-wide text-[--color-text]">
                                        MANTLE CLAIM ID
                                    </p>
                                    <p className="truncate text-xs font-semibold text-[--color-text]">
                                        {parentClaimExternalReferenceId}
                                    </p>
                                </div>
                                <div className="flex items-start ">
                                    <div title={addButtonTitle}>
                                        <Dropdown>
                                            <DropdownTrigger
                                                asChild
                                                disabled={addButtonDisabled}
                                            >
                                                <Button
                                                    size="auto"
                                                    variant="normal"
                                                    aria-label="Add notes to grouped claim"
                                                    className="size-6 rounded-md border-0 bg-primary p-0 hover:bg-primary"
                                                >
                                                    <PlusIcon className="size-3 stroke-white" />
                                                </Button>
                                            </DropdownTrigger>
                                            <DropdownContent
                                                align="end"
                                                width={220}
                                            >
                                                <div className="max-h-[280px] overflow-y-auto">
                                                    {eligibleNotesForBulkClaimQuery.isLoading ? (
                                                        <div className="px-3 py-2 text-sm text-textSec">
                                                            Loading notes...
                                                        </div>
                                                    ) : null}
                                                    {eligibleNotesForBulkClaimQuery.error ? (
                                                        <div className="px-3 py-2 text-sm text-danger">
                                                            Could not load
                                                            eligible notes.
                                                        </div>
                                                    ) : null}
                                                    {!eligibleNotesForBulkClaimQuery.isLoading &&
                                                    !eligibleNotesForBulkClaimQuery.error &&
                                                    eligibleNotesForBulkClaim.length ===
                                                        0 ? (
                                                        <div className="px-3 py-2 text-sm text-textSec">
                                                            No client notes
                                                            available to add.
                                                        </div>
                                                    ) : null}
                                                    {eligibleNotesForBulkClaim.map(
                                                        (note) => (
                                                            <DropdownItem
                                                                key={
                                                                    note.note_id
                                                                }
                                                                onClick={() =>
                                                                    handleToggleNoteToAdd(
                                                                        note.note_id
                                                                    )
                                                                }
                                                                isMulti
                                                                itemValue={
                                                                    note.note_id
                                                                }
                                                                values={
                                                                    selectedNoteIdsToAdd
                                                                }
                                                            >
                                                                <div className="flex min-w-0 flex-col gap-0.5">
                                                                    <span className="text-sm">
                                                                        {formatDate(
                                                                            note.date_of_service
                                                                        )}
                                                                    </span>
                                                                    <span className="text-caption text-textSec">
                                                                        {
                                                                            NoteLabels[
                                                                                note
                                                                                    .note_type
                                                                            ]
                                                                        }
                                                                    </span>
                                                                    <div className="text-caption text-textSec">
                                                                        {getCptCodeToDisplay(
                                                                            {
                                                                                noteType:
                                                                                    note.note_type,
                                                                                cptCode:
                                                                                    note.cpt_code,
                                                                                unitCalculation:
                                                                                    note
                                                                                        .note_metadata
                                                                                        ?.unit_calculation,
                                                                            }
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </DropdownItem>
                                                        )
                                                    )}
                                                </div>
                                                <div className="border-t px-2 py-2">
                                                    <Button
                                                        size="auto"
                                                        className="w-full justify-center"
                                                        onClick={handleAddNotes}
                                                        disabled={
                                                            selectedNoteIdsToAdd.length ===
                                                                0 ||
                                                            addNotesToBulkClaim.isLoading
                                                        }
                                                    >
                                                        {addNotesToBulkClaim.isLoading
                                                            ? "Adding..."
                                                            : "Add"}
                                                    </Button>
                                                </div>
                                            </DropdownContent>
                                        </Dropdown>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-3 flex flex-col gap-2">
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-[10px] font-semibold tracking-wide text-textSec">
                                        TOTAL CHARGE
                                    </span>
                                    <span className="text-xs font-semibold text-[--color-text]">
                                        {showMoneyInAppFormat(
                                            parentClaimData.data.data
                                                .parent_claim
                                                .charge_amount_total
                                        )}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-[10px] font-semibold tracking-wide text-textSec whitespace-nowrap">
                                        GROUP STATUS
                                    </span>
                                    <Dropdown>
                                        <DropdownTrigger
                                            asChild
                                            disabled={
                                                claimStatusDropdownDisabled
                                            }
                                        >
                                            <Button
                                                size="auto"
                                                variant="normal"
                                                iconRight={
                                                    <ChevronDownIcon
                                                        stroke={
                                                            parentClaimStatusStyles?.strokeColor
                                                        }
                                                        className="size-3 shrink-0"
                                                    />
                                                }
                                                className={cn(
                                                    "h-5 rounded-full border-0 px-2 py-0 text-[10px] font-semibold capitalize whitespace-nowrap",
                                                    parentClaimStatusStyles?.className
                                                )}
                                            >
                                                {updateClaimStatus.isLoading
                                                    ? "Updating..."
                                                    : removeEnumUnderscore(
                                                          parentClaimStatus ??
                                                              ""
                                                      ) || "Unknown"}
                                            </Button>
                                        </DropdownTrigger>
                                        <DropdownContent
                                            align="end"
                                            width="auto"
                                        >
                                            {CLAIM_STATUS_OPTIONS.map(
                                                (status) => {
                                                    const statusStyles =
                                                        getClaimStatusTagStylesForDropdown(
                                                            status
                                                        );

                                                    return (
                                                        <DropdownItem
                                                            key={status}
                                                            onClick={() =>
                                                                handleUpdateParentClaimStatus(
                                                                    status
                                                                )
                                                            }
                                                            isSelected={
                                                                parentClaimStatus ===
                                                                status
                                                            }
                                                        >
                                                            <span
                                                                className={cn(
                                                                    "inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize whitespace-nowrap",
                                                                    statusStyles.className
                                                                )}
                                                            >
                                                                {removeEnumUnderscore(
                                                                    status
                                                                )}
                                                            </span>
                                                        </DropdownItem>
                                                    );
                                                }
                                            )}
                                        </DropdownContent>
                                    </Dropdown>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2.5 px-2 pb-1 pt-2">
                            {childClaimsWithDetails.map(
                                ({
                                    childClaim,
                                    claim,
                                    isLoading,
                                    invoiceStatus,
                                }) => {
                                    const isActive =
                                        selectedChildClaim?.childClaim
                                            .claim_id === childClaim.claim_id;
                                    const isSelectedForRebill =
                                        selectedChildClaimIdSet?.has(
                                            childClaim.claim_id
                                        ) ?? false;

                                    let claimAmountToDisplay = "--";
                                    if (!isLoading && claim) {
                                        claimAmountToDisplay =
                                            showMoneyInAppFormat(
                                                claim.charge_amount ?? 0
                                            );
                                    }

                                    const cptDisplay =
                                        getClaimCptCodesCompact(claim);

                                    const billStatusLabel =
                                        removeEnumUnderscore(
                                            BillStatusLabels[
                                                invoiceStatus as BillStatus
                                            ] ??
                                                invoiceStatus ??
                                                "Unknown"
                                        );

                                    return (
                                        <div
                                            key={childClaim.claim_id}
                                            className={cn(
                                                "relative flex items-center gap-2 rounded-lg border px-2.5 py-2.5 bg-transparent border-stroke! hover:bg-[--card-bg-dark]",
                                                {
                                                    "border-[--color-stroke] bg-[--card-bg-dark] text-[--color-text] shadow-sm":
                                                        isActive &&
                                                        !isSelectedForRebill,
                                                    "border-primary! bg-[--color-link-interaction] text-[--color-text] shadow-sm":
                                                        isSelectedForRebill,
                                                }
                                            )}
                                        >
                                            {canRebillChildClaims ? (
                                                <Checkbox
                                                    label=""
                                                    name={`rebill-${childClaim.claim_id}`}
                                                    value={childClaim.claim_id}
                                                    isChecked={
                                                        isSelectedForRebill
                                                    }
                                                    handleChange={() =>
                                                        toggleChildClaimSelection(
                                                            childClaim.claim_id
                                                        )
                                                    }
                                                />
                                            ) : null}
                                            <Button
                                                type="button"
                                                size="auto"
                                                variant="normal"
                                                onClick={() =>
                                                    handleSelectClaim(
                                                        childClaim
                                                    )
                                                }
                                                className="group min-w-0 flex-1 cursor-pointer border-0 bg-transparent p-0 text-left text-sm outline-none"
                                            >
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <span className="text-xs font-semibold">
                                                            {formatDate(
                                                                childClaim.date_of_service
                                                            )}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center justify-between gap-2 mt-1">
                                                        {cptDisplay || (
                                                            <span className="text-xs text-[--color-text]">
                                                                {childClaim.cpt_code ||
                                                                    "--"}
                                                            </span>
                                                        )}
                                                        <span className="text-xs font-medium shrink-0">
                                                            {
                                                                claimAmountToDisplay
                                                            }
                                                        </span>
                                                    </div>
                                                    <div className="mt-1 flex items-center justify-between gap-2">
                                                        {getStatusTag(
                                                            childClaim.claim_status
                                                        )}
                                                        <span
                                                            className={cn(
                                                                "rounded-full px-1.5 py-0 text-[10px] shrink-0",
                                                                getBillStatusClassName(
                                                                    invoiceStatus
                                                                )
                                                            )}
                                                        >
                                                            {`Bill: ${billStatusLabel}`}
                                                        </span>
                                                    </div>
                                                </div>
                                            </Button>
                                            {!hasChildSelection &&
                                            hasMultipleChildClaims ? (
                                                <ActionButton
                                                    type="button"
                                                    size="auto"
                                                    variant="normal"
                                                    actionTitle="Detach claim from group"
                                                    className="absolute right-1 top-1 z-10 rounded border-0 p-1 outline-none"
                                                    aria-label={`Detach claim ${childClaim.claim_id} from the group`}
                                                    onClick={() => {
                                                        if (
                                                            !canRebillChildClaims ||
                                                            !hasMultipleChildClaims
                                                        ) {
                                                            return;
                                                        }
                                                        setStoredRebillTargetIds(
                                                            [
                                                                childClaim.claim_id,
                                                            ]
                                                        );
                                                    }}
                                                    disabled={
                                                        childRebillActionsDisabled
                                                    }
                                                >
                                                    <TimesIcon className="size-3.5" />
                                                </ActionButton>
                                            ) : null}
                                        </div>
                                    );
                                }
                            )}
                        </div>
                        {canRebillChildClaims && hasChildSelection ? (
                            <div className="border-t px-2 py-2.5">
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="primary"
                                        className="flex-1 justify-center"
                                        onClick={() => {
                                            if (bulkRebillDisabled) {
                                                return;
                                            }
                                            setStoredRebillTargetIds(
                                                selectedChildClaimIds
                                            );
                                        }}
                                        disabled={bulkRebillDisabled}
                                        title={
                                            selectionIncludesAllChildren
                                                ? "Leave at least one claim in the group to detach"
                                                : undefined
                                        }
                                    >
                                        Detach from group
                                    </Button>
                                </div>
                            </div>
                        ) : null}
                    </Card>

                    <Card classNames="min-w-0 bg-[--card-bg-gray] p-2">
                        {selectedChildClaim?.isLoading && <Loader />}
                        {!selectedChildClaim?.isLoading &&
                            activeClaimId ===
                                selectedChildClaim?.childClaim.claim_id &&
                            selectedChildClaim?.claim && (
                                <ViewClaim isEmbedded />
                            )}
                        {!selectedChildClaim?.isLoading &&
                            activeClaimId !==
                                selectedChildClaim?.childClaim.claim_id && (
                                <Loader />
                            )}
                        {!selectedChildClaim?.isLoading &&
                            activeClaimId ===
                                selectedChildClaim?.childClaim.claim_id &&
                            !selectedChildClaim?.claim && (
                                <ListState
                                    isError
                                    emptyMessage="Could not load selected child claim"
                                    stateHelperText="Select another claim from the sidebar."
                                />
                            )}
                    </Card>
                </div>
            </div>
        </>
    );
}
